import React, { useMemo, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { AddRegular, ArrowLeft12Filled, SaveRegular } from "@fluentui/react-icons";
import { Button, Card, Field, Input, useRestoreFocusTarget } from "@fluentui/react-components";

import { Stack } from "@/components/stack";
import { AutosizeTextarea } from "@/components/autosize-textarea";

import { ExampleLanguageLabel, FallbackRuleCard } from "./components/fallback-rule-card";
import { ExitPlaybookRuleEditorDialog } from "./components/exit-playbook-rule-editor-dialog";
import { usePlaybookRuleEditorStyles } from "./styles";

import { ClauseData, FallbackRule } from "@/types/risk-analysis";

interface PlaybookRuleEditorProps {
  playbookRule?: ClauseData;
  onGoBack: (playbookRule?: ClauseData) => void;
}

export function PlaybookRuleEditor({ playbookRule, onGoBack }: PlaybookRuleEditorProps) {
  const styles = usePlaybookRuleEditorStyles();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const restoreExitTargetAttribute = useRestoreFocusTarget();
  const [exitDialogOpen, setExitDialogOpen] = useState<[boolean, (() => void)?]>([false, undefined]);

  const [clauseTopic, setClauseTopic] = useState<string>(playbookRule?.clauseTopic || "");
  const [customerRules, setCustomerRules] = useState<FallbackRule[]>(playbookRule?.customerRules || []);
  const [preferredLanguage, setPreferredLanguage] = useState<string>(playbookRule?.preferredLanguage || "");
  const [preferredLanguageTitle, setPreferredLanguageTitle] = useState<string>(
    playbookRule?.preferredLanguageTitle || ""
  );
  const [preferredLanguageExample, setPreferredLanguageExample] = useState<string>(
    playbookRule?.preferredLanguageExample || ""
  );

  const hasUnsavedChanges = useMemo(() => {
    const initialClauseTopic = playbookRule?.clauseTopic || "";
    const initialPreferredLanguage = playbookRule?.preferredLanguage || "";
    const initialPreferredLanguageTitle = playbookRule?.preferredLanguageTitle || "";
    const initialPreferredLanguageExample = playbookRule?.preferredLanguageExample || "";
    const initialCustomerRules = playbookRule?.customerRules || [];
    return (
      clauseTopic !== initialClauseTopic ||
      preferredLanguage !== initialPreferredLanguage ||
      preferredLanguageTitle !== initialPreferredLanguageTitle ||
      preferredLanguageExample !== initialPreferredLanguageExample ||
      JSON.stringify(customerRules) !== JSON.stringify(initialCustomerRules)
    );
  }, [clauseTopic, preferredLanguage, preferredLanguageTitle, preferredLanguageExample, customerRules, playbookRule]);

  // @NOTE: this is FAR from an ideal solution
  // but until we have the time to properly setup
  // react-router than it will serve its purpose
  const containerRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(containerRef, (event) => {
    const allowedIds = [
      "risk-analysis",
      "questions",
      "revisions",
      "configurations",
      "yellowpad-playbooks",
      "my-playbooks",
      "settings",
    ];
    event.preventDefault();
    let el = event.target as HTMLElement | null;
    if (el?.id && allowedIds.includes(el?.id)) {
      handleExitEditor(() => el.click());
    }
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onGoBack({
      ...(playbookRule ?? {}),
      id: playbookRule?.id || uuidv4(),
      clauseTopic,
      preferredLanguage,
      preferredLanguageTitle,
      preferredLanguageExample,
      customerRules,
    } as ClauseData);
  };

  const handleAddFallbackRule = () => {
    setCustomerRules((prev) => [
      ...prev,
      {
        id: uuidv4(),
        title: "",
        prompt: "",
        rule: "",
        example: "",
        order: prev.length,
      } as FallbackRule,
    ]);
  };

  const handleUpdateFallbackRule = (index: number, field: string, value: string) => {
    setCustomerRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)));
  };

  const handleRemoveFallbackRule = (index: number) => {
    setCustomerRules((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const handleExitEditor = (cb?: () => void) => {
    if (hasUnsavedChanges) {
      setExitDialogOpen([true, cb]);
    } else {
      cb ? cb() : onGoBack();
    }
  };

  const handleConfirmExit = () => {
    setExitDialogOpen([false, undefined]);
    exitDialogOpen[1] ? exitDialogOpen[1]() : onGoBack();
  };

  const handleSwapDefaultRule = (fallbackRule: FallbackRule, index: number) => {
    setCustomerRules((prev) => [
      ...prev.slice(0, index),
      { ...fallbackRule, title: preferredLanguageTitle, rule: preferredLanguage, example: preferredLanguageExample },
      ...prev.slice(index + 1),
    ]);
    setPreferredLanguageTitle(fallbackRule.title);
    setPreferredLanguage(fallbackRule.rule);
    setPreferredLanguageExample(fallbackRule.example);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (active.id !== over.id) {
      setCustomerRules((prev) => {
        const oldIndex = prev.findIndex((rule) => rule.id === active.id);
        const newIndex = prev.findIndex((rule) => rule.id === over.id);
        const newRules = arrayMove(prev, oldIndex, newIndex);
        return newRules.map((rule, index) => ({
          ...rule,
          order: index,
        }));
      });
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <Button
        appearance="transparent"
        icon={<ArrowLeft12Filled />}
        onClick={() => handleExitEditor()}
        {...restoreExitTargetAttribute}
      >
        Playbook Rule {playbookRule ? "Editor" : "Creator"}
      </Button>
      <form onSubmit={handleSubmit}>
        <Card className={styles.card}>
          <Stack align="start" gap="2rem">
            <Field required label="Clause Topic" style={{ flexGrow: 1 }}>
              <Input
                required
                name="clause-topic"
                value={clauseTopic}
                onChange={(e) => setClauseTopic(e.target.value)}
              />
            </Field>
            <Button type="submit" appearance="primary" tabIndex={-1} icon={<SaveRegular />}>
              Save
            </Button>
          </Stack>
          <Field required label="Rule Title">
            <Input
              required
              name="rule-title"
              value={preferredLanguageTitle}
              onChange={(e) => setPreferredLanguageTitle(e.target.value)}
            />
          </Field>
          <Field required label="Rule Description">
            <AutosizeTextarea
              required
              name="rule-description"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
            />
          </Field>
          <Field label={<ExampleLanguageLabel label="Rule Example Language" />}>
            <AutosizeTextarea
              name="rule-example"
              value={preferredLanguageExample}
              onChange={(e) => setPreferredLanguageExample(e.target.value)}
            />
          </Field>
          <Stack direction="column" gap="1rem">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={customerRules} strategy={verticalListSortingStrategy}>
                {customerRules
                  .sort((a, b) => a.order - b.order)
                  .map((fallbackRule, index) => (
                    <FallbackRuleCard
                      key={fallbackRule.id}
                      id={fallbackRule.id}
                      fallbackRule={fallbackRule}
                      onEdit={(field, value) => handleUpdateFallbackRule(index, field, value)}
                      onSwap={() => handleSwapDefaultRule(fallbackRule, index)}
                      onDelete={() => handleRemoveFallbackRule(index)}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </Stack>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            className={styles.addFallbackRuleButton}
            onClick={handleAddFallbackRule}
          >
            Add Fallback
          </Button>
        </Card>
      </form>
      <ExitPlaybookRuleEditorDialog
        playbookRule={playbookRule}
        open={exitDialogOpen[0]}
        onAccept={handleConfirmExit}
        onOpenChange={(_, data) => setExitDialogOpen([data.open, undefined])}
      />
    </div>
  );
}
